// The landing page (EN `/` and DE `/de/`) and every component it renders.
//
// This module is deliberately split out of the 7k-line `pages.tsx` monolith.
// `pages.tsx` exports ~40 page components and pulls in Convex hooks, Clerk auth,
// the search index, anonymous-state, learning-stats and the rest. Because every
// route's split component chunk statically imported that one shared module, the
// landing's initial JS dragged in ALL of it even though it only renders
// `HomePage`. Moving the landing cluster into its own leaf module lets the two
// index routes import ONLY this file, so the landing's chunk no longer carries
// the other 39 pages' code (the bulk of the "unused JavaScript" on `/`).
//
// Everything here is presentational and translation-driven; the only client
// logic is the Testimonials autoplay carousel (moved verbatim, SSR-guarded).
// The shared catalog leaves (`CourseCard`, `LessonRow`) live in
// `catalog-cards.tsx` so this file and `pages.tsx` share one source of truth.
import {
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Code2,
  Compass,
  GaugeCircle,
  Layers,
  Lock,
  MapPin,
  Quote,
  Rocket,
  ShieldCheck,
  Sparkles,
  Star,
  Terminal,
  Workflow,
} from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { PointerEvent as ReactPointerEvent, ReactNode } from 'react'
import { FaqAccordion } from './faq-accordion'
import { LessonRow } from './catalog-cards'
import { ProBadge } from './pro-badge'
import { PricingPlans } from './pricing/pricing-plans'
import { buildFaqJsonLd } from '../lib/seo'
import { useCatalog } from '../content/catalog-context'
import { interpolate, localizedPath, useTranslation } from '../lib/i18n'
import { pricingCopyValues } from '../lib/pricing'
import type { Course } from '../content/catalog'

type Testimonial = {
  name: string
  role: string
  title: string
  quote: string
  initials: string
}

// Person identity stays language-neutral here; the localized role/title/quote
// copy lives in the dictionary (`pages.testimonials.cards`) and is merged by
// index inside the carousel so EN and DE both render native copy.
const testimonialPeople: Array<{ name: string; initials: string }> = [
  { name: 'Lara Reinhardt', initials: 'LR' },
  { name: 'Marco Brunner', initials: 'MB' },
  { name: 'Tanja Nef', initials: 'TN' },
  { name: 'Sebastian Vogt', initials: 'SV' },
  { name: 'Nicole Hofer', initials: 'NH' },
  { name: 'Daniel Keller', initials: 'DK' },
  { name: 'Priya Schneider', initials: 'PS' },
  { name: 'Florian Bachmann', initials: 'FB' },
  { name: 'Sandra Weiss', initials: 'SW' },
  { name: 'Tobias Lang', initials: 'TL' },
  { name: 'Eva Moser', initials: 'EM' },
  { name: 'Lukas Frei', initials: 'LF' },
]

export function HomePage() {
  const { locale, t } = useTranslation()
  const catalog = useCatalog()
  const published = catalog.lessons.filter(
    (lesson) => lesson.status === 'published',
  )
  const featuredLesson = published[0] ?? catalog.lessons[0]
  const nextLessons = published.slice(1, 4)
  // Derived from the catalog so the hero can never claim more free lessons
  // than the paywall actually leaves open (Courses 1-2 today).
  const freeLessonCount = catalog.lessons.filter(
    (lesson) => lesson.access === 'free',
  ).length

  return (
    <main className="home-page">
      <section className="hero-section home-hero" data-hero-sentinel>
        <div className="hero-atmosphere" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
        <div className="hero-inner home-hero-inner">
          <div className="hero-copy home-hero-copy">
            <span className="eyebrow">{t('pages.home.heroEyebrow')}</span>
            <h1>
              <span className="hero-title-lead">
                {t('pages.home.heroTitleA')}
              </span>{' '}
              <br className="hero-title-break" />
              {t('pages.home.heroTitleB')}{' '}
              <span className="hero-title-tail">
                <span className="hero-painted-underline">
                  {t('pages.home.heroTitleUnderline')}
                </span>
                .
              </span>
            </h1>
            <p>{t('pages.home.heroLead')}</p>
            <div className="hero-actions">
              <a
                className="primary-button"
                href={localizedPath('register', locale)}
                data-cta="hero"
                data-cta-kind="signup"
              >
                {t('common.signUpCta')}
                <ArrowRight size={18} />
              </a>
            </div>
            <div
              className="hero-proof-row"
              aria-label={t('pages.home.heroMetricsLabel')}
            >
              <span>
                <strong>{catalog.lessons.length}</strong>
                {t('pages.home.metricLessons')}
              </span>
              <span>
                <strong>{freeLessonCount}</strong>
                {t('pages.home.metricFreeLessons')}
              </span>
              <span>
                <strong>{catalog.resources.length}</strong>
                {t('pages.home.metricResources')}
              </span>
            </div>
          </div>
          <div
            className="hero-product-stage"
            aria-label={t('pages.home.stagePreviewLabel')}
          >
            <div className="product-stage-top">
              <div>
                <span>{t('pages.home.stageOs')}</span>
                <strong>{t('pages.home.stageCockpit')}</strong>
              </div>
              <small>{t('pages.home.stagePublicFirst')}</small>
            </div>
            <div className="product-window">
              <div className="product-hero-panel">
                <div>
                  <span className="eyebrow">
                    {t('pages.home.stageLivePath')}
                  </span>
                  <h2>{t('pages.home.stagePathTitle')}</h2>
                </div>
                <strong>{t('pages.home.stageLessons')}</strong>
              </div>
              <div className="product-metrics-grid">
                <MetricTile
                  label={t('pages.home.stageRoiLabel')}
                  value="12"
                  text={t('pages.home.stageRoiText')}
                />
                <MetricTile
                  label={t('pages.home.stageToolLabel')}
                  value="6"
                  text={t('pages.home.stageToolText')}
                />
                <MetricTile
                  label={t('pages.home.stageLaunchLabel')}
                  value={published.length}
                  text={t('pages.home.stageLaunchText')}
                />
              </div>
              <div className="featured-lesson-card">
                <span>{featuredLesson.number}</span>
                <div>
                  <strong>{featuredLesson.title}</strong>
                  <p>{featuredLesson.outcome}</p>
                </div>
                <a
                  href={localizedPath('lesson', locale, {
                    courseSlug: featuredLesson.courseSlug,
                    lessonSlug: featuredLesson.slug,
                  })}
                  aria-label={t('pages.home.startLessonCta', {
                    title: featuredLesson.title,
                  })}
                >
                  {t('pages.common.start')}
                </a>
              </div>
              <div className="next-lesson-stack">
                {nextLessons.map((lesson) => (
                  <a
                    key={lesson.slug}
                    href={localizedPath('lesson', locale, {
                      courseSlug: lesson.courseSlug,
                      lessonSlug: lesson.slug,
                    })}
                  >
                    <span>{lesson.number}</span>
                    <strong>{lesson.title}</strong>
                    <small>{lesson.duration}</small>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <ProblemSection />

      <FounderNote />

      <div
        id="loesung"
        className="home-solution-cluster"
        aria-label={t('pages.home.solutionLabel')}
      >
        <div
          className="home-proof-strip"
          aria-label={t('pages.home.proofWhyLabel')}
        >
          <div>
            <strong>{t('pages.home.proofLessons')}</strong>
            <span>{t('pages.home.proofLessonsText')}</span>
          </div>
          <div>
            <strong>{t('pages.home.proofResources')}</strong>
            <span>{t('pages.home.proofResourcesText')}</span>
          </div>
          <div>
            <strong>{t('pages.home.proofGerman')}</strong>
            <span>{t('pages.home.proofGermanText')}</span>
          </div>
        </div>

        <DarkFeatureBand />

        <section className="home-section home-system-section">
          <div className="section-heading">
            <span className="eyebrow">{t('pages.home.systemEyebrow')}</span>
            <h2>{t('pages.home.systemTitle')}</h2>
            <p>{t('pages.home.systemLead')}</p>
            <div className="hero-actions" style={{ marginTop: '22px' }}>
              <a
                className="primary-button"
                href={localizedPath('course', locale, {
                  courseSlug: 'foundations',
                })}
                data-cta="hero"
                data-cta-kind="starter_path"
              >
                {t('pages.common.starterPathOpen')}
              </a>
            </div>
          </div>
          <div className="safety-layout">
            <div className="value-grid">
              <ValueCard
                icon={<Sparkles size={24} />}
                title={t('pages.home.valueToolClarityTitle')}
                text={t('pages.home.valueToolClarityText')}
              />
              <ValueCard
                icon={<ClipboardList size={24} />}
                title={t('pages.home.valueWorkflowTitle')}
                text={t('pages.home.valueWorkflowText')}
              />
              <ValueCard
                icon={<ShieldCheck size={24} />}
                title={t('pages.home.valueRoiTitle')}
                text={t('pages.home.valueRoiText')}
              />
            </div>
            <SafetyVisual />
          </div>
        </section>

        <SplitCta />
        <CourseRail />
        <LessonRoadmap />
      </div>

      <Testimonials />

      <HomePricing />

      <HowItWorks />

      <HomeFaq />
    </main>
  )
}

/**
 * Homepage pricing: the SAME shared pricing block as /pricing (toggle, AR 3:4
 * cards, guarantee, founding-member capture while billing is not live), with a
 * link to the full comparison + billing FAQ on the pricing page.
 */
function HomePricing() {
  const { locale, t } = useTranslation()
  return (
    <section
      id="preise"
      className="home-section pricing-section"
      aria-labelledby="home-pricing-title"
    >
      <div className="pricing-inner">
        <div className="section-heading pricing-heading">
          <span className="eyebrow">{t('pages.pricing.eyebrow')}</span>
          <h2 id="home-pricing-title">{t('pages.pricing.title')}</h2>
          <p>{t('pages.pricing.lead')}</p>
        </div>
        <PricingPlans surface="home_pricing" />
        <a className="ghost-button pricing-compare-link" href={localizedPath('pricing', locale)}>
          {t('pages.pricing.compareLink')}
          <ArrowRight size={16} aria-hidden="true" />
        </a>
      </div>
    </section>
  )
}

function HowItWorks() {
  const { locale, t } = useTranslation()
  const steps = [
    { title: t('pages.howItWorks.step1Title'), text: t('pages.howItWorks.step1Text') },
    { title: t('pages.howItWorks.step2Title'), text: t('pages.howItWorks.step2Text') },
    { title: t('pages.howItWorks.step3Title'), text: t('pages.howItWorks.step3Text') },
    { title: t('pages.howItWorks.step4Title'), text: t('pages.howItWorks.step4Text') },
  ]
  return (
    <section
      id="ablauf"
      className="home-section howitworks-section"
      aria-label={t('pages.howItWorks.label')}
    >
      <div className="section-heading">
        <span className="eyebrow">{t('pages.howItWorks.eyebrow')}</span>
        <h2>{t('pages.howItWorks.title')}</h2>
        <p>{t('pages.howItWorks.lead')}</p>
      </div>
      <ol className="howitworks-grid">
        {steps.map((step, index) => (
          <li key={step.title} className="howitworks-step">
            <span className="howitworks-number" aria-hidden="true">
              {(index + 1).toString().padStart(2, '0')}
            </span>
            <h3>{step.title}</h3>
            <p>{step.text}</p>
          </li>
        ))}
      </ol>
      <div className="howitworks-cta">
        <a
          className="ghost-button"
          href={localizedPath('course', locale, { courseSlug: 'foundations' })}
        >
          {t('pages.common.starterPathOpen')}
          <ArrowRight size={16} />
        </a>
      </div>
    </section>
  )
}

function HomeFaq() {
  const { locale, t, dict } = useTranslation()
  // Billing answers quote live prices from the single pricing config.
  const copyValues = pricingCopyValues(locale)
  const items = dict.pages.homeFaq.items.map((item) => ({
    question: interpolate(item.question, copyValues),
    answer: interpolate(item.answer, copyValues),
  }))
  return (
    <section
      id="faq"
      className="home-section faq-section"
      aria-label={t('common.faqLabel')}
    >
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(buildFaqJsonLd(items, locale)),
        }}
      />
      <div className="section-heading">
        <span className="eyebrow">{t('pages.homeFaq.eyebrow')}</span>
        <h2>{t('pages.homeFaq.title')}</h2>
        <p>{t('pages.homeFaq.lead')}</p>
      </div>
      <FaqAccordion items={items} />
    </section>
  )
}

function FounderNote() {
  const { t } = useTranslation()
  return (
    <section
      id="gruender"
      className="home-section founder-section"
      aria-label={t('pages.founder.label')}
    >
      <div className="founder-grid">
        <figure className="founder-portrait">
          <picture>
            <source
              type="image/webp"
              srcSet="/founder-nico-420.webp 420w, /founder-nico-640.webp 640w, /founder-nico.webp 800w"
              sizes="(max-width: 980px) 360px, 420px"
            />
            <img
              src="/founder-nico.jpg"
              srcSet="/founder-nico-420.jpg 420w, /founder-nico-640.jpg 640w, /founder-nico.jpg 800w"
              sizes="(max-width: 980px) 360px, 420px"
              alt={t('pages.founder.imageAlt')}
              width={800}
              height={1200}
              loading="lazy"
              decoding="async"
            />
          </picture>
          <figcaption className="founder-namecard">
            <span className="founder-dot" aria-hidden="true" />
            {t('pages.founder.nameCard')}
          </figcaption>
        </figure>
        <div className="founder-copy">
          <span className="eyebrow founder-eyebrow">
            {t('pages.founder.label')}
          </span>
          <Quote className="founder-quote-mark" size={38} aria-hidden="true" />
          <h2 className="founder-heading">{t('pages.founder.heading')}</h2>
          <p>{t('pages.founder.p1')}</p>
          <p>{t('pages.founder.p2')}</p>
          <p>
            {t('pages.founder.p3Pre')}
            <strong>{t('pages.founder.p3Strong1')}</strong>
            {t('pages.founder.p3Mid')}
            <strong>{t('pages.founder.p3Strong2')}</strong>
            {t('pages.founder.p3Post')}
          </p>
          <div className="founder-tags">
            <span className="founder-tag">
              <Rocket size={14} aria-hidden="true" /> {t('pages.founder.tag1')}
            </span>
            <span className="founder-tag">
              <Code2 size={14} aria-hidden="true" /> {t('pages.founder.tag2')}
            </span>
            <span className="founder-tag">
              <MapPin size={14} aria-hidden="true" /> {t('pages.founder.tag3')}
            </span>
          </div>
        </div>
      </div>
    </section>
  )
}

function ProblemSection() {
  const { locale, t } = useTranslation()
  return (
    <section
      id="problem"
      className="home-section problem-section"
      aria-label={t('pages.problem.label')}
    >
      <div className="section-heading">
        <span className="eyebrow">{t('pages.problem.label')}</span>
        <h2>{t('pages.problem.title')}</h2>
        <p>{t('pages.problem.lead')}</p>
      </div>
      <div className="problem-grid">
        <ProblemCard
          icon={<Lock size={22} />}
          title={t('pages.problem.card1Title')}
          text={t('pages.problem.card1Text')}
        />
        <ProblemCard
          icon={<Sparkles size={22} />}
          title={t('pages.problem.card2Title')}
          text={t('pages.problem.card2Text')}
        />
        <ProblemCard
          icon={<Terminal size={22} />}
          title={t('pages.problem.card3Title')}
          text={t('pages.problem.card3Text')}
        />
        <ProblemCard
          icon={<Compass size={22} />}
          title={t('pages.problem.card4Title')}
          text={t('pages.problem.card4Text')}
        />
      </div>
      <div className="problem-bridge">
        <p>{t('pages.problem.bridge')}</p>
        <a
          className="primary-button"
          href={localizedPath('course', locale, { courseSlug: 'foundations' })}
        >
          {t('pages.problem.cta')}
          <ArrowRight size={18} />
        </a>
      </div>
    </section>
  )
}

function ProblemCard({
  icon,
  title,
  text,
}: {
  icon: ReactNode
  title: string
  text: string
}) {
  return (
    <article className="problem-card">
      <span className="problem-card-icon" aria-hidden="true">
        {icon}
      </span>
      <h3>{title}</h3>
      <p>{text}</p>
    </article>
  )
}

function DarkFeatureBand() {
  const { t } = useTranslation()
  return (
    <section className="band band-ink">
      <div className="band-inner">
        <div className="dark-feature-band">
          <div className="dark-feature-visual" aria-hidden="true">
            <div className="mock-device">
              <div className="mock-device-head">
                <span>{t('pages.darkBand.pathLabel')}</span>
                <span>{t('pages.darkBand.addLesson')}</span>
              </div>
              <div className="mock-card is-accent">
                <span>{t('pages.darkBand.today')}</span>
                <strong>{t('pages.darkBand.todayTitle')}</strong>
                <small>{t('pages.darkBand.todayMeta')}</small>
              </div>
              <div className="mock-card">
                <span>{t('pages.darkBand.thisWeek')}</span>
                <strong>{t('pages.darkBand.thisWeekTitle')}</strong>
                <small>{t('pages.darkBand.thisWeekMeta')}</small>
              </div>
              <div className="mock-card">
                <span>{t('pages.darkBand.capstone')}</span>
                <strong>{t('pages.darkBand.capstoneTitle')}</strong>
                <small>{t('pages.darkBand.capstoneMeta')}</small>
              </div>
            </div>
          </div>
          <div className="dark-feature-copy">
            <span className="eyebrow on-ink">
              <Sparkles size={12} /> {t('pages.darkBand.eyebrow')}
            </span>
            <h2>{t('pages.darkBand.title')}</h2>
            <p>{t('pages.darkBand.lead')}</p>
            <div className="feature-list">
              <FeatureItem
                icon={<Compass size={20} />}
                title={t('pages.darkBand.feature1Title')}
                text={t('pages.darkBand.feature1Text')}
              />
              <FeatureItem
                icon={<Workflow size={20} />}
                title={t('pages.darkBand.feature2Title')}
                text={t('pages.darkBand.feature2Text')}
              />
              <FeatureItem
                icon={<GaugeCircle size={20} />}
                title={t('pages.darkBand.feature3Title')}
                text={t('pages.darkBand.feature3Text')}
              />
              <FeatureItem
                icon={<Layers size={20} />}
                title={t('pages.darkBand.feature4Title')}
                text={t('pages.darkBand.feature4Text')}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

function FeatureItem({
  icon,
  title,
  text,
}: {
  icon: ReactNode
  title: string
  text: string
}) {
  return (
    <div className="feature-item">
      <span className="feature-item-icon">{icon}</span>
      <div>
        <h3>{title}</h3>
        <p>{text}</p>
      </div>
    </div>
  )
}

function SafetyVisual() {
  const { t } = useTranslation()
  return (
    <div className="safety-visual" aria-hidden="true">
      <div className="safety-visual-mock">
        <strong>{t('pages.splitCta.roiOverview')}</strong>
        <div className="safety-visual-row">
          <span>RW</span>
          <div>
            <small>{t('pages.splitCta.roadmapWorkflow')}</small>
            <strong>{t('pages.splitCta.codexSetup')}</strong>
          </div>
          <span>+18h</span>
        </div>
        <div className="safety-visual-row">
          <span>SP</span>
          <div>
            <small>{t('pages.splitCta.specReview')}</small>
            <strong>{t('pages.splitCta.agenticApi')}</strong>
          </div>
          <span>+12h</span>
        </div>
        <div className="safety-visual-row">
          <span>CR</span>
          <div>
            <small>{t('pages.splitCta.capstoneRun')}</small>
            <strong>{t('pages.splitCta.capstoneTitle')}</strong>
          </div>
          <span>+34h</span>
        </div>
      </div>
    </div>
  )
}

function SplitCta() {
  const { locale, t } = useTranslation()
  return (
    <section className="split-cta" aria-label={t('pages.splitCta.label')}>
      <div className="split-cta-copy">
        <span className="eyebrow">{t('pages.splitCta.eyebrow')}</span>
        <h2>
          {t('pages.splitCta.titleA')}
          <br />
          {t('pages.splitCta.titleB')}
        </h2>
        <p>{t('pages.splitCta.lead')}</p>
        <a className="primary-button" href={localizedPath('courses', locale)}>
          {t('pages.splitCta.cta')}
          <ArrowRight size={18} />
        </a>
      </div>
      <div className="split-cta-visual" aria-hidden="true">
        <div className="split-cta-card">
          <strong className="title">{t('pages.splitCta.cardTitle')}</strong>
          <div className="big-value">
            18h<small>{t('pages.splitCta.cardSetup')}</small>
          </div>
          <div className="row">
            <span>{t('pages.splitCta.row1')}</span>
            <strong>+8h</strong>
          </div>
          <div className="row">
            <span>{t('pages.splitCta.row2')}</span>
            <strong>+6h</strong>
          </div>
          <div className="row">
            <span>{t('pages.splitCta.row3')}</span>
            <strong>+4h</strong>
          </div>
          <div className="row">
            <span>{t('pages.splitCta.row4')}</span>
            <strong>{t('pages.splitCta.row4Value')}</strong>
          </div>
          <span className="cta">{t('pages.splitCta.cardCta')}</span>
        </div>
      </div>
    </section>
  )
}

function TestimonialCard({
  testimonial,
  ariaHidden,
}: {
  testimonial: Testimonial
  ariaHidden?: boolean
}) {
  return (
    <article
      className="testimonial-card"
      aria-hidden={ariaHidden ? 'true' : undefined}
    >
      <div className="testimonial-stars" aria-hidden="true">
        {Array.from({ length: 5 }).map((_, index) => (
          <Star
            key={index}
            size={15}
            strokeWidth={1.5}
            color="var(--accent-main)"
            fill="var(--accent-main)"
          />
        ))}
      </div>
      <strong className="testimonial-title">{testimonial.title}</strong>
      <p className="testimonial-quote">{testimonial.quote}</p>
      <footer className="testimonial-footer">
        <span className="testimonial-avatar" aria-hidden="true">
          {testimonial.initials}
        </span>
        <span className="testimonial-person">
          <strong className="testimonial-name">{testimonial.name}</strong>
          <span className="testimonial-role">{testimonial.role}</span>
        </span>
      </footer>
    </article>
  )
}

function wrapCarouselOffset(value: number, setWidth: number) {
  if (setWidth <= 0) return Math.max(0, value)
  let next = value
  while (next >= setWidth) next -= setWidth
  while (next < 0) next += setWidth
  return next
}

function Testimonials() {
  const { t, dict } = useTranslation()
  const testimonials: Testimonial[] = testimonialPeople.map((person, index) => {
    const card = dict.pages.testimonials.cards[index]
    return {
      name: person.name,
      initials: person.initials,
      role: card.role,
      title: card.title,
      quote: card.quote,
    }
  })
  const carouselRef = useRef<HTMLDivElement | null>(null)
  const trackRef = useRef<HTMLDivElement | null>(null)
  const offsetRef = useRef(0)
  const pausedRef = useRef(false)
  const draggingRef = useRef(false)
  const dragStartXRef = useRef(0)
  const dragStartOffsetRef = useRef(0)
  const resumeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  const getSetWidth = useCallback(() => {
    const track = trackRef.current
    return track ? track.scrollWidth / 2 : 0
  }, [])

  const setCarouselOffset = useCallback(
    (value: number) => {
      const carousel = carouselRef.current
      const next = wrapCarouselOffset(value, getSetWidth())
      offsetRef.current = next
      if (carousel) carousel.scrollLeft = next
    },
    [getSetWidth],
  )

  const syncOffsetFromScroll = useCallback(() => {
    const carousel = carouselRef.current
    if (!carousel) return

    const next = wrapCarouselOffset(carousel.scrollLeft, getSetWidth())
    offsetRef.current = next
    if (Math.abs(carousel.scrollLeft - next) > 0.5) {
      carousel.scrollLeft = next
    }
  }, [getSetWidth])

  const pause = useCallback(() => {
    pausedRef.current = true
    if (resumeTimeoutRef.current) {
      clearTimeout(resumeTimeoutRef.current)
      resumeTimeoutRef.current = null
    }
  }, [])

  const scheduleResume = useCallback(() => {
    if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current)
    resumeTimeoutRef.current = setTimeout(() => {
      pausedRef.current = false
    }, 700)
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const reduceMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches
    if (reduceMotion) return

    const track = trackRef.current
    const carousel = carouselRef.current
    if (!track || !carousel) return

    const speed = 75 // px per second
    let frame = 0
    let last = performance.now()

    const tick = (now: number) => {
      const delta = (now - last) / 1000
      last = now
      if (!draggingRef.current && !pausedRef.current) {
        setCarouselOffset(offsetRef.current + speed * delta)
      } else {
        setCarouselOffset(offsetRef.current)
      }
      frame = window.requestAnimationFrame(tick)
    }

    // Autoplay is gated so it never burns the main thread when the visitor
    // cannot see it: it starts only after the page has gone idle once, while
    // the carousel is in (or near) the viewport, and while the tab is visible.
    // When any of those stops being true the rAF loop is fully torn down (it
    // stops re-scheduling), so a backgrounded tab or a carousel scrolled off
    // screen costs nothing. The visible behaviour - seamless looping autoplay
    // at the same speed once on screen - is unchanged. User input (drag, wheel,
    // touch, scroll) below works whether or not autoplay happens to be running.
    let idleReady = false
    let inView = typeof IntersectionObserver === 'undefined'
    let tabVisible =
      typeof document === 'undefined' || document.visibilityState === 'visible'
    let running = false

    const syncLoop = () => {
      const shouldRun = idleReady && inView && tabVisible
      if (shouldRun === running) return
      running = shouldRun
      if (running) {
        last = performance.now()
        frame = window.requestAnimationFrame(tick)
      } else {
        window.cancelAnimationFrame(frame)
        frame = 0
      }
    }

    // Defer the first start to idle so autoplay never competes with hydration.
    let idleHandle = 0
    let idleTimer: ReturnType<typeof setTimeout> | null = null
    const markIdle = () => {
      idleReady = true
      syncLoop()
    }
    if (typeof window.requestIdleCallback === 'function') {
      idleHandle = window.requestIdleCallback(markIdle, { timeout: 1000 })
    } else {
      idleTimer = setTimeout(markIdle, 200)
    }

    // Pause whenever the tab is hidden, resume when it returns.
    const onVisibilityChange = () => {
      tabVisible = document.visibilityState === 'visible'
      syncLoop()
    }
    document.addEventListener('visibilitychange', onVisibilityChange)

    // Only animate while the carousel is on (or near) screen.
    let observer: IntersectionObserver | null = null
    if (typeof IntersectionObserver !== 'undefined') {
      observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) inView = entry.isIntersecting
          syncLoop()
        },
        { rootMargin: '200px 0px' },
      )
      observer.observe(carousel)
    } else {
      // No IntersectionObserver (older browsers / SSR-less test envs): treat
      // the carousel as always in view so autoplay still works.
      syncLoop()
    }

    // Wheel / trackpad browsing. React attaches `onWheel` as a passive
    // listener, so `preventDefault` would be ignored - we attach a native
    // non-passive listener instead. Only horizontal-dominant scrolls
    // (trackpad two-finger swipe, shift+wheel) move the carousel, so a normal
    // vertical wheel keeps scrolling the page and is never trapped. The rAF
    // loop above keeps autoplay and user input on the same scroll position.
    const onWheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return
      event.preventDefault()
      pause()
      setCarouselOffset(carousel.scrollLeft + event.deltaX)
      scheduleResume()
    }
    carousel.addEventListener('wheel', onWheel, { passive: false })

    return () => {
      window.cancelAnimationFrame(frame)
      if (idleTimer) clearTimeout(idleTimer)
      if (idleHandle && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleHandle)
      }
      document.removeEventListener('visibilitychange', onVisibilityChange)
      observer?.disconnect()
      carousel.removeEventListener('wheel', onWheel)
      if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current)
    }
  }, [pause, scheduleResume, setCarouselOffset])

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return
    draggingRef.current = true
    setIsDragging(true)
    pause()
    dragStartXRef.current = event.clientX
    dragStartOffsetRef.current = offsetRef.current
    event.preventDefault()
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return
    const dx = event.clientX - dragStartXRef.current
    setCarouselOffset(dragStartOffsetRef.current - dx)
  }

  const endDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return
    draggingRef.current = false
    setIsDragging(false)
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    scheduleResume()
  }

  return (
    <section
      id="stimmen"
      className="home-section testimonials-section"
      aria-label={t('pages.testimonials.label')}
    >
      <div className="section-heading">
        <span className="eyebrow">{t('pages.testimonials.label')}</span>
        <h2>{t('pages.testimonials.title')}</h2>
        <p>{t('pages.testimonials.lead')}</p>
      </div>
      <div
        ref={carouselRef}
        className={'testimonial-carousel' + (isDragging ? ' is-dragging' : '')}
        role="region"
        aria-label={t('pages.testimonials.label')}
        tabIndex={0}
        onFocusCapture={pause}
        onBlurCapture={scheduleResume}
        onTouchStart={pause}
        onTouchEnd={scheduleResume}
        onScroll={syncOffsetFromScroll}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        <div className="testimonial-track" ref={trackRef}>
          {testimonials.map((testimonial) => (
            <TestimonialCard key={testimonial.name} testimonial={testimonial} />
          ))}
          {testimonials.map((testimonial) => (
            <TestimonialCard
              key={'dup-' + testimonial.name}
              testimonial={testimonial}
              ariaHidden
            />
          ))}
        </div>
      </div>
    </section>
  )
}

function MetricTile({
  label,
  value,
  text,
}: {
  label: string
  value: string | number
  text: string
}) {
  return (
    <div className="metric-tile">
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{text}</small>
    </div>
  )
}
function ValueCard({
  icon,
  title,
  text,
}: {
  icon: ReactNode
  title: string
  text: string
}) {
  return (
    <article className="value-card">
      <span>{icon}</span>
      <div>
        <h3>{title}</h3>
        <p>{text}</p>
      </div>
    </article>
  )
}

function CourseRail() {
  const { locale, t } = useTranslation()
  const catalog = useCatalog()
  return (
    <section className="home-section academy-path-section">
      <div className="academy-path-copy">
        <span className="eyebrow">{t('pages.courseRail.eyebrow')}</span>
        <h2>{t('pages.courseRail.title')}</h2>
        <p>{t('pages.courseRail.lead')}</p>
        <a className="primary-button" href={localizedPath('courses', locale)}>
          {t('pages.courseRail.cta')}
          <ArrowRight size={16} />
        </a>
      </div>
      <div className="academy-path-grid">
        {catalog.courses.map((course) => (
          <HomeCourseCard key={course.slug} course={course} />
        ))}
      </div>
    </section>
  )
}

function HomeCourseCard({ course }: { course: Course }) {
  const { locale, t } = useTranslation()
  return (
    <a
      className="academy-path-card"
      href={localizedPath('course', locale, { courseSlug: course.slug })}
    >
      <span>{course.order.toString().padStart(2, '0')}</span>
      <h3>{course.title}</h3>
      <p>{course.subtitle}</p>
      <div>
        {course.tier === 'pro' ? <ProBadge /> : null}
        <small>
          {course.lessonCount} {t('pages.common.lessonsWord')}
        </small>
        <small>
          {course.estimatedMinutes} {t('pages.common.minutesShort')}
        </small>
      </div>
    </a>
  )
}

function LessonRoadmap() {
  const { locale, t } = useTranslation()
  const catalog = useCatalog()
  const pageCount = catalog.courses.length
  const [page, setPage] = useState(0)
  const course = catalog.courses[page]
  const lessons = catalog
    .getLessonsForCourse(course.slug)
    .slice(0, course.lessonCount)

  const goPrev = () =>
    setPage((current) => (current - 1 + pageCount) % pageCount)
  const goNext = () => setPage((current) => (current + 1) % pageCount)

  return (
    <section className="home-section lesson-command-section">
      <div className="lesson-command-head">
        <div className="section-heading">
          <span className="eyebrow">{t('pages.roadmap.eyebrow')}</span>
          <h2>{t('pages.roadmap.title')}</h2>
          <p>{t('pages.roadmap.lead')}</p>
        </div>
      </div>
      <div className="lesson-command-grid" key={course.slug}>
        <a
          className="featured-roadmap-card"
          href={localizedPath('course', locale, { courseSlug: course.slug })}
        >
          <div className="featured-roadmap-inner">
            <span>
              {t('pages.roadmap.courseOf', {
                current: page + 1,
                total: pageCount,
              })}
              {course.tier === 'pro' ? <ProBadge className="is-inline" /> : null}
            </span>
            <h3>{course.title}</h3>
            <p>{course.subtitle}</p>
            <small className="featured-roadmap-meta">
              {course.lessonCount} {t('pages.common.lessonsWord')} &middot;{' '}
              {course.estimatedMinutes} {t('pages.common.minutesShort')}
            </small>
            <strong>
              {t('pages.roadmap.startNow')} <ArrowRight size={14} />
            </strong>
          </div>
        </a>
        <div className="lesson-command-list">
          {lessons.map((lesson, index) => (
            <LessonRow
              key={lesson.slug}
              lesson={lesson}
              displayLabel={`${page + 1}.${index + 1}`}
            />
          ))}
        </div>
      </div>
      <div className="roadmap-pagination roadmap-pagination-below">
        <button
          type="button"
          className="roadmap-nav-btn"
          aria-label={t('pages.roadmap.prevLessons')}
          onClick={goPrev}
        >
          <ChevronLeft size={18} aria-hidden="true" />
        </button>
        <div
          className="roadmap-dots"
          role="tablist"
          aria-label={t('pages.roadmap.coursesLabel')}
        >
          {catalog.courses.map((item, index) => (
            <button
              key={item.slug}
              type="button"
              role="tab"
              className="roadmap-dot"
              aria-label={t('pages.roadmap.courseDotLabel', {
                index: index + 1,
                title: item.title,
              })}
              aria-current={index === page ? 'true' : undefined}
              aria-selected={index === page}
              onClick={() => setPage(index)}
            >
              {index + 1}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="roadmap-nav-btn"
          aria-label={t('pages.roadmap.nextLessons')}
          onClick={goNext}
        >
          <ChevronRight size={18} aria-hidden="true" />
        </button>
        <p className="roadmap-page-label" aria-live="polite">
          {t('pages.roadmap.courseOf', {
            current: page + 1,
            total: pageCount,
          })}
        </p>
      </div>
    </section>
  )
}
